import * as React from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseApp';

// Map Firebase error codes to generic user-facing messages
// Prevents user enumeration and internal detail leaks
function getAuthErrorMessage(err) {
  switch (err.code) {
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password does not meet requirements.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [userProfile, setUserProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Listen for auth state changes
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Fetch user profile from Firestore
        try {
          const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (profileDoc.exists()) {
            setUserProfile(profileDoc.data());
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign up with email and password
  const signup = async (email, password, displayName) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update display name
      await updateProfile(firebaseUser, { displayName });

      // Create user profile in Firestore
      const userProfileData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userProfileData);
      setUserProfile(userProfileData);

      return { success: true, user: firebaseUser };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Sign out
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      return { success: true };
    } catch (err) {
      const message = getAuthErrorMessage(err);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Update user profile
  const ALLOWED_PROFILE_FIELDS = ['displayName', 'bio', 'website', 'location', 'profileComplete'];
  const MAX_FIELD_LENGTH = 500;

  const updateUserProfile = async (updates) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Whitelist allowed fields and enforce length limits
      const sanitizedUpdates = {};
      for (const key of ALLOWED_PROFILE_FIELDS) {
        if (key in updates) {
          const val = updates[key];
          if (typeof val === 'string' && val.length > MAX_FIELD_LENGTH) continue;
          sanitizedUpdates[key] = val;
        }
      }

      const updatedProfile = {
        ...userProfile,
        ...sanitizedUpdates,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, 'users', user.uid), updatedProfile, { merge: true });
      setUserProfile(updatedProfile);

      // Update Firebase Auth profile if displayName changed
      if (updates.displayName) {
        await updateProfile(user, { displayName: updates.displayName });
      }

      return { success: true };
    } catch (err) {
      const message = 'Failed to update profile. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Check if user profile is complete
  const isProfileComplete = React.useMemo(() => {
    if (!userProfile) return false;
    return userProfile.profileComplete === true;
  }, [userProfile]);

  // Check if this is a new user (just signed up, needs profile setup)
  const needsProfileSetup = React.useMemo(() => {
    if (!user || loading) return false;
    return !isProfileComplete;
  }, [user, loading, isProfileComplete]);

  const value = {
    user,
    userProfile,
    loading,
    error,
    signup,
    login,
    logout,
    updateUserProfile,
    isAuthenticated: !!user,
    isProfileComplete,
    needsProfileSetup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
